import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave, faPlay, faTrash, faCog, faSearch, faRefresh,
  faChartLine, faChartBar, faTable, faTimes, faSpinner,
  faLightbulb, faEye, faLink, faCalculator, faPlus
} from '@fortawesome/free-solid-svg-icons';
import { MetricComponent, UnitEconomicsTemplate } from '../types/templateDesigner';
import dataHeaderService, { DataHeader, DataHeaderCategory } from '../services/dataHeaderService';
import templateService from '../services/templateService';
import { Canvas, PropertiesPanel, FormulaModal, PreviewPanel } from './VisualTemplateDesignerV2Components';

interface VisualTemplateDesignerV2Props {
  template?: UnitEconomicsTemplate;
  onSave?: (template: UnitEconomicsTemplate) => void;
  onExecute?: (template: UnitEconomicsTemplate) => void;
}

interface Connection {
  id: string;
  from: string;
  to: string;
  formula?: string;
  label?: string;
}

interface VisualizationConfig {
  type: 'kpi' | 'bar' | 'line' | 'pie' | 'table';
  componentIds: string[];
  title: string;
}

export const VisualTemplateDesignerV2: React.FC<VisualTemplateDesignerV2Props> = ({
  template,
  onSave,
  onExecute
}) => {
  // Core state
  const [components, setComponents] = useState<MetricComponent[]>(template?.components || []);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [selectedComponent, setSelectedComponent] = useState<MetricComponent | null>(null);
  const [templateName, setTemplateName] = useState(template?.name || 'New Template');

  // Dynamic headers
  const [headerCategories, setHeaderCategories] = useState<DataHeaderCategory[]>([]);
  const [loadingHeaders, setLoadingHeaders] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonth, setSelectedMonth] = useState<string>(new Date().toISOString().slice(0, 7));
  const [includeAllMonths, setIncludeAllMonths] = useState(false);

  // Connection state
  const [connectingFrom, setConnectingFrom] = useState<string | null>(null);
  const [showFormulaModal, setShowFormulaModal] = useState(false);
  const [activeConnection, setActiveConnection] = useState<Connection | null>(null);

  // Execution state
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState<any>(null);

  // Visualization state
  const [visualizations, setVisualizations] = useState<VisualizationConfig[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const canvasRef = useRef<HTMLDivElement>(null);
  const [draggedItem, setDraggedItem] = useState<any>(null);

  // Load dynamic headers on mount and when month/includeAllMonths changes
  useEffect(() => {
    loadHeaders();
  }, [selectedMonth, includeAllMonths]);

  const loadHeaders = async () => {
    setLoadingHeaders(true);
    try {
      const month = includeAllMonths ? undefined : selectedMonth;
      const categories = await dataHeaderService.getAllHeaders(month, includeAllMonths);
      setHeaderCategories(categories);
    } catch (error) {
      console.error('Error loading headers:', error);
    } finally {
      setLoadingHeaders(false);
    }
  };

  // Filter headers by search
  const filteredCategories = searchQuery
    ? headerCategories.map(cat => ({
        ...cat,
        headers: cat.headers.filter(h =>
          h.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          h.description?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(cat => cat.headers.length > 0)
    : headerCategories;

  // Get icon for data type
  const getDataIcon = (header: DataHeader) => {
    if (header.dataType === 'currency') return '💰';
    if (header.dataType === 'percentage') return '📊';
    if (header.dataType === 'number') return '🔢';
    return '📈';
  };

  // Handle drag start from library
  const handleDragStart = (header: DataHeader, category: string) => {
    setDraggedItem({ ...header, category });
  };

  // Handle drop on canvas
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedItem || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const newComponent: MetricComponent = {
      id: `comp_${Date.now()}`,
      type: draggedItem.source?.includes('cost') ? 'cost_source' : 'input_metric',
      name: draggedItem.displayName,
      label: draggedItem.displayName,
      description: draggedItem.description || '',
      position: { x, y },
      config: {
        dataType: draggedItem.dataType,
        unit: draggedItem.unit || '',
        metricType: draggedItem.metricType || draggedItem.name
      },
      inputs: [],
      outputs: [],
      metadata: {
        category: draggedItem.category,
        tags: [draggedItem.source, draggedItem.id]
      }
    };

    setComponents([...components, newComponent]);
    setDraggedItem(null);
  }, [draggedItem, components]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // Component management
  const handleComponentClick = (component: MetricComponent) => {
    setSelectedComponent(component);
  };

  const handleDeleteComponent = (componentId: string) => {
    setComponents(components.filter(c => c.id !== componentId));
    setConnections(connections.filter(c => c.from !== componentId && c.to !== componentId));
    if (selectedComponent?.id === componentId) {
      setSelectedComponent(null);
    }
  };

  const handleUpdateComponent = (componentId: string, updates: Partial<MetricComponent>) => {
    setComponents(components.map(c =>
      c.id === componentId ? { ...c, ...updates } : c
    ));
    if (selectedComponent?.id === componentId) {
      setSelectedComponent({ ...selectedComponent, ...updates });
    }
  };

  // Connection management
  const handleStartConnection = (componentId: string) => {
    console.log('🟢 handleStartConnection called with:', componentId);
    setConnectingFrom(componentId);
    console.log('🟢 connectingFrom state set to:', componentId);
  };

  const handleCompleteConnection = (toComponentId: string) => {
    console.log('🔵 handleCompleteConnection called');
    console.log('🔵 Parameters:', {
      toComponentId,
      currentConnectingFrom: connectingFrom,
      isSameComponent: connectingFrom === toComponentId
    });

    if (!connectingFrom || connectingFrom === toComponentId) {
      console.log('❌ Aborting connection:', {
        reason: !connectingFrom ? 'No source component' : 'Same component',
        connectingFrom,
        toComponentId
      });
      setConnectingFrom(null);
      return;
    }

    console.log('✅ Valid connection detected!');
    console.log('🔗 Creating connection object:', { from: connectingFrom, to: toComponentId });

    const newConnection: Connection = {
      id: `conn_${Date.now()}`,
      from: connectingFrom,
      to: toComponentId
    };

    console.log('📝 Connection object created:', newConnection);
    console.log('🎯 Setting activeConnection state...');
    setActiveConnection(newConnection);

    console.log('🎯 Opening formula modal (setShowFormulaModal(true))...');
    setShowFormulaModal(true);

    console.log('🧹 Clearing connectingFrom state...');
    setConnectingFrom(null);

    console.log('✨ Connection flow complete - modal should be visible now');
  };

  const handleSaveConnection = (formula: string, label?: string) => {
    if (!activeConnection) return;

    const connectionWithFormula = { ...activeConnection, formula, label };
    setConnections([...connections, connectionWithFormula]);

    // NOTE: The target component stays as an input_metric data point — we do
    // NOT overwrite it with the connection formula. Otherwise the formula
    // references itself (e.g. ={Revenue}/{Customer Count} stored on Customer
    // Count produces a cyclic 52000/0 = Infinity). The formula result lives
    // on the connection (computed server-side, shown in the label popover).

    setShowFormulaModal(false);
    setActiveConnection(null);
  };

  // Auto-generate visualizations
  const handleGenerateVisualizations = () => {
    const newViz: VisualizationConfig[] = [];

    // Generate KPIs for all metrics
    const metricComponents = components.filter(c =>
      c.type === 'input_metric' || c.type === 'cost_source' || c.type === 'calculated_metric'
    );

    if (metricComponents.length > 0) {
      newViz.push({
        type: 'kpi',
        componentIds: metricComponents.map(c => c.id),
        title: 'Key Metrics'
      });
    }

    // Generate charts for calculated metrics
    const calculatedMetrics = components.filter(c => c.type === 'calculated_metric');
    if (calculatedMetrics.length > 0) {
      newViz.push({
        type: 'bar',
        componentIds: calculatedMetrics.map(c => c.id),
        title: 'Calculated Metrics'
      });
    }

    // Generate table for all data
    if (components.length > 0) {
      newViz.push({
        type: 'table',
        componentIds: components.map(c => c.id),
        title: 'All Metrics'
      });
    }

    setVisualizations(newViz);
    setShowPreview(true);
  };

  // Save template
  const handleSave = () => {
    const newTemplate: UnitEconomicsTemplate = {
      id: template?.id || `template_${Date.now()}`,
      name: templateName,
      description: `Dynamic template with ${components.length} components`,
      category: 'custom',
      version: '2.0.0',
      author: 'User',
      isPublic: false,
      tags: ['dynamic', 'custom'],
      layout: {
        width: 1200,
        height: 800,
        gridSize: 20,
        zoom: 1
      },
      components,
      executionOrder: [], // Will be calculated by backend
      formulas: connections.reduce((acc, conn) => {
        if (conn.formula) acc[conn.to] = conn.formula;
        return acc;
      }, {} as any),
      displays: {
        kpis: visualizations.filter(v => v.type === 'kpi').flatMap(v => v.componentIds),
        charts: visualizations.filter(v => v.type === 'bar' || v.type === 'line' || v.type === 'pie').flatMap(v => v.componentIds),
        tables: visualizations.filter(v => v.type === 'table').flatMap(v => v.componentIds)
      },
      settings: {
        refreshInterval: 300000,
        dateRange: 'current_month',
        currency: 'USD'
      },
      createdAt: template?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    onSave?.(newTemplate);
  };

  // Execute template
  const handleExecute = async () => {
    setIsExecuting(true);
    try {
      // Build template and execute
      const execTemplate: UnitEconomicsTemplate = {
        id: template?.id || `template_${Date.now()}`,
        name: templateName,
        description: 'Execution template',
        category: 'custom',
        version: '2.0.0',
        author: 'User',
        isPublic: false,
        tags: [],
        layout: { width: 1200, height: 800, gridSize: 20, zoom: 1 },
        components,
        executionOrder: [],
        formulas: connections.reduce((acc, conn) => {
          if (conn.formula) acc[conn.to] = conn.formula;
          return acc;
        }, {} as any),
        displays: {
          kpis: [],
          charts: [],
          tables: []
        },
        settings: {
          refreshInterval: 300000,
          dateRange: 'current_month',
          currency: 'USD'
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await onExecute?.(execTemplate);

      // Run real execution on the backend against the current draft template.
      // Pass connections so the server can evaluate per-connection formulas
      // and return their values keyed by connection id.
      const execution = await templateService.executeDraftTemplate(
        { ...execTemplate, connections } as any,
        {},
        { month: selectedMonth }
      );

      setExecutionResult(execution);

      // Auto-generate visualizations
      handleGenerateVisualizations();
    } catch (error) {
      console.error('Execution error:', error);
      alert(`Execution failed: ${(error as Error).message}`);
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div className="flex h-[800px] bg-gray-50 border border-gray-300 rounded overflow-hidden">
      {/* Component Library Panel */}
      <ComponentLibrary
        categories={filteredCategories}
        loading={loadingHeaders}
        searchQuery={searchQuery}
        selectedMonth={selectedMonth}
        includeAllMonths={includeAllMonths}
        onSearchChange={setSearchQuery}
        onMonthChange={setSelectedMonth}
        onIncludeAllMonthsChange={setIncludeAllMonths}
        onRefresh={loadHeaders}
        onDragStart={handleDragStart}
        getDataIcon={getDataIcon}
      />

      {/* Main Canvas Area */}
      <div className="flex-1 flex flex-col">
        {/* Toolbar */}
        <Toolbar
          templateName={templateName}
          onTemplateNameChange={setTemplateName}
          onSave={handleSave}
          onExecute={handleExecute}
          onGenerateVisualizations={handleGenerateVisualizations}
          onTogglePreview={() => setShowPreview(!showPreview)}
          isExecuting={isExecuting}
          showPreview={showPreview}
          hasComponents={components.length > 0}
        />

        {/* Canvas */}
        <div className="flex-1 flex">
          <Canvas
            ref={canvasRef}
            components={components}
            connections={connections}
            selectedComponent={selectedComponent}
            connectingFrom={connectingFrom}
            executionResult={executionResult}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onComponentClick={handleComponentClick}
            onComponentDelete={handleDeleteComponent}
            onComponentUpdate={handleUpdateComponent}
            onStartConnection={handleStartConnection}
            onCompleteConnection={handleCompleteConnection}
          />

          {/* Properties Panel */}
          {selectedComponent && (
            <PropertiesPanel
              component={selectedComponent}
              onUpdate={(updates) => handleUpdateComponent(selectedComponent.id, updates)}
              onClose={() => setSelectedComponent(null)}
            />
          )}
        </div>

        {/* Preview Panel */}
        {showPreview && executionResult && (
          <PreviewPanel
            visualizations={visualizations}
            components={components}
            executionResult={executionResult}
            onClose={() => setShowPreview(false)}
          />
        )}
      </div>

      {/* Formula Modal */}
      {showFormulaModal && activeConnection && (
        <FormulaModal
          connection={activeConnection}
          components={components}
          onSave={handleSaveConnection}
          onClose={() => {
            setShowFormulaModal(false);
            setActiveConnection(null);
          }}
        />
      )}
    </div>
  );
};

// ============================================================================
// COMPONENT LIBRARY
// ============================================================================

interface ComponentLibraryProps {
  categories: DataHeaderCategory[];
  loading: boolean;
  searchQuery: string;
  selectedMonth: string;
  includeAllMonths: boolean;
  onSearchChange: (query: string) => void;
  onMonthChange: (month: string) => void;
  onIncludeAllMonthsChange: (include: boolean) => void;
  onRefresh: () => void;
  onDragStart: (header: DataHeader, category: string) => void;
  getDataIcon: (header: DataHeader) => string;
}

const ComponentLibrary: React.FC<ComponentLibraryProps> = ({
  categories,
  loading,
  searchQuery,
  selectedMonth,
  includeAllMonths,
  onSearchChange,
  onMonthChange,
  onIncludeAllMonthsChange,
  onRefresh,
  onDragStart,
  getDataIcon
}) => {
  return (
    <div className="w-80 bg-white border-r border-gray-300 overflow-y-auto">
      {/* Header */}
      <div className="p-3 border-b border-gray-300 sticky top-0 bg-white z-10">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-gray-900">
            📊 Dynamic Data Library
          </h3>
          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-1 text-gray-600 hover:text-purple-600 transition-colors"
            title="Refresh headers"
          >
            <FontAwesomeIcon icon={faRefresh} spin={loading} className="text-xs" />
          </button>
        </div>
        <p className="text-xs text-gray-600 mb-2">
          Drag any metric onto the canvas
        </p>

        {/* Month Selector */}
        <div className="mb-2">
          <label className="flex items-center text-xs text-gray-700 mb-1">
            <input
              type="checkbox"
              checked={includeAllMonths}
              onChange={(e) => onIncludeAllMonthsChange(e.target.checked)}
              className="mr-2"
            />
            Include all months
          </label>
          {!includeAllMonths && (
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => onMonthChange(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            />
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search metrics..."
            className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded"
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="p-8 text-center">
          <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-purple-600 mb-2" />
          <p className="text-xs text-gray-600">Loading data headers...</p>
        </div>
      )}

      {/* Categories */}
      {!loading && categories.map((category) => (
        <div key={category.category} className="border-b border-gray-200">
          <div className="p-3 bg-gray-50">
            <h4 className="text-xs font-semibold text-gray-800">{category.displayName}</h4>
            <p className="text-xs text-gray-500">{category.description}</p>
            <div className="text-xs text-gray-400 mt-1">{category.headers.length} metrics</div>
          </div>
          <div className="p-2 space-y-1">
            {category.headers.map((header) => (
              <div
                key={header.id}
                draggable
                onDragStart={() => onDragStart(header, category.category)}
                className="p-2 bg-white border border-gray-200 rounded cursor-move hover:bg-purple-50 hover:border-purple-400 transition-all group"
              >
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{getDataIcon(header)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-900 truncate">
                      {header.displayName}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{header.unit || header.dataType}</div>
                  </div>
                </div>
                {header.description && (
                  <div className="text-xs text-gray-400 mt-1 line-clamp-2">
                    {header.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Empty State */}
      {!loading && categories.length === 0 && (
        <div className="p-8 text-center">
          <FontAwesomeIcon icon={faLightbulb} className="text-4xl text-gray-300 mb-2" />
          <p className="text-xs text-gray-600">No data found</p>
          <p className="text-xs text-gray-500 mt-1">
            Try adding business metrics or adjusting your search
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// TOOLBAR
// ============================================================================

interface ToolbarProps {
  templateName: string;
  onTemplateNameChange: (name: string) => void;
  onSave: () => void;
  onExecute: () => void;
  onGenerateVisualizations: () => void;
  onTogglePreview: () => void;
  isExecuting: boolean;
  showPreview: boolean;
  hasComponents: boolean;
}

const Toolbar: React.FC<ToolbarProps> = ({
  templateName,
  onTemplateNameChange,
  onSave,
  onExecute,
  onGenerateVisualizations,
  onTogglePreview,
  isExecuting,
  showPreview,
  hasComponents
}) => {
  return (
    <div className="bg-white border-b border-gray-300 p-2 flex items-center justify-between">
      <div className="flex items-center space-x-2">
        <input
          type="text"
          value={templateName}
          onChange={(e) => onTemplateNameChange(e.target.value)}
          className="px-3 py-1 text-xs border border-gray-300 rounded font-medium"
          placeholder="Template name..."
        />
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={onGenerateVisualizations}
          disabled={!hasComponents}
          className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Auto-generate KPIs, charts, and tables"
        >
          <FontAwesomeIcon icon={faChartLine} className="mr-1" />
          Generate Viz
        </button>
        <button
          onClick={onTogglePreview}
          disabled={!hasComponents}
          className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faEye} className="mr-1" />
          {showPreview ? 'Hide' : 'Preview'}
        </button>
        <button
          onClick={onSave}
          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <FontAwesomeIcon icon={faSave} className="mr-1" />
          Save
        </button>
        <button
          onClick={onExecute}
          disabled={isExecuting || !hasComponents}
          className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
        >
          {isExecuting ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="mr-1 animate-spin" />
              Executing...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faPlay} className="mr-1" />
              Execute
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// CANVAS (Continued in next file part due to size)
// ============================================================================

export default VisualTemplateDesignerV2;
